/* Cali Raised LED cart drawer - 2026-08-06. Paired with crled-cart-drawer.css/.liquid
 *
 * ADD TO CART -> intercepted, posted to /cart/add.js, drawer opens.
 * BUY NOW     -> intercepted too, but after the add we redirect to /checkout.
 *
 * Keeps the header cart count live without editing sections/header.liquid.
 * Keeps Cart.js in sync via CartJS.getCart().
 */
(function () {
  'use strict';

  var drawer, overlay, body, foot, subEl, hCount, busy = false;
  var CART_LINK = 'a[href$="/cart"], a[href$="/cart/"]';
  // The theme's own counters: .cart-icon-top in the header, .cart-count on the cart page.
  var THEME_COUNT = '.cart-icon-top, .cart-count, [class*="cart-count"], [class*="cart_count"]';

  function money(c) { return '$' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Redo's "Free Returns + Package Protection" line.
   *
   * Identified by Redo's OWN product fields, never a hard-coded variant id: the coverage
   * product carries 96 variants (one per price tier - 3% of cart, min $1 / max $55, plus
   * $2.98 flat return coverage) and Redo picks one per cart, but they all share
   * sku "x-redo", vendor "re:do" and the one handle.
   *
   * It is a real Shopify line item that Redo adds on PAGE LOAD (not at add-to-cart), so
   * it can be absent from a drawer opened straight after ADD TO CART and appear later.
   *
   * We render it WITHOUT a quantity stepper - "package protection x3" is meaningless and
   * was previously reachable - and with a labelled Remove instead. Removal via
   * /cart/change.js sticks across reloads; Redo does not force it back.
   *
   * When Redo's own toggle starts injecting at [data-crled-redo] (blocked until Redo
   * removes us from their coverage experiment), switch this row to hidden and let the
   * toggle represent it - otherwise there will be two competing controls. */
  function isCoverage(i) {
    return i.sku === 'x-redo' || i.vendor === 're:do' ||
      i.handle === 'free-unlimited-return-valid-in-us';
  }

  /* Count of what the shopper actually put in the cart - coverage excluded.
   *
   * Every badge and count is driven from THIS number, not cart.item_count, for three
   * reasons discovered the hard way (2026-08-11):
   *   1. RACE-IMMUNITY. Redo re-tiers its coverage line (remove old variant, add new)
   *      at unpredictable times after any cart mutation. item_count flaps 4->3->4/5
   *      during the swap, and no fixed-delay re-read reliably lands after it - a
   *      settled read still showed 4 while /cart.js later said 5. The REAL-item count
   *      is identical on both sides of a swap, so the badge simply cannot flap.
   *   2. CONSISTENCY. Redo already rewrites counters matching [class*="cart-count"]
   *      site-wide with its own coverage-excluding number (measured: it stomped our
   *      setCount value and even a test value within a tick). Counting the same way
   *      means our surfaces and theirs agree instead of disagreeing by one.
   *   3. SHOPPER MENTAL MODEL. Someone who added 3 products expects "3".
   * snippets/crled-cart-badge-patch.liquid applies the same rule to the label-shaped
   * "Cart (N)" counters, and snippets/top-site-nav.liquid + snippets/mobile-side.liquid
   * server-render the same number - change them together or surfaces disagree. */
  function realCount(cart) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (i) { if (!isCoverage(i)) n += i.quantity; });
    return n;
  }

  function open() {
    if (!drawer) return;
    overlay.hidden = false;
    // Force reflow so the transition runs. NOT requestAnimationFrame: rAF is throttled
    // or never fires when the page is not actively rendering.
    void overlay.offsetWidth;
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('crled-cd-open');
  }

  function close() {
    if (!drawer) return;
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('crled-cd-open');
    setTimeout(function () { overlay.hidden = true; }, 300);
  }

  /* Give every cart link a LIVE count without adding a second number.
   *
   * The theme already prints <span class="cart-icon-top"> in the header, but it is
   * server-rendered ONLY - verified it does not update on an AJAX cart change, not even
   * after CartJS.getCart(). That was fine before this drawer existed, because adding to
   * cart navigated to /cart and re-rendered the whole page. Now that we keep the shopper
   * in place it goes stale, which is the entire reason a live count is needed here.
   *
   * Creating our own unconditionally put TWO numbers in the header, and they disagreed:
   * the theme's "3" (server-rendered, counting Redo's coverage line) beside our "2"
   * (fetched before Redo had added it). So ADOPT the theme's element where one exists and
   * drive that; only create a badge on links that have no counter of their own (e.g. the
   * mobile menu link, which renders just an icon and the word "Cart").
   *
   * The mobile header icon's bubble (snippets/mobile-side.liquid) is pre-tagged with
   * data-cd-count server-side and its class avoids the "cart-count" substring - see the
   * comment there for why. mountBadges leaves it alone via the first guard.
   *
   * Do not add .crled-cd-count to an adopted element - it should keep the theme's own
   * styling and position. */
  function mountBadges() {
    document.querySelectorAll(CART_LINK).forEach(function (a) {
      if (a.querySelector('[data-cd-count]')) return;
      var existing = a.querySelector(THEME_COUNT);
      if (existing) { existing.setAttribute('data-cd-count', ''); return; }
      if (getComputedStyle(a).position === 'static') a.style.position = 'relative';
      var b = document.createElement('span');
      b.className = 'crled-cd-count';
      b.setAttribute('data-cd-count', '');
      b.hidden = true;
      a.appendChild(b);
    });
  }

  /* ---------------------------------------------------------------------------------
   * /cart.js DISCOUNT FIELDS - read this before touching the maths below.
   *
   * For a CART-LEVEL discount (a code like "10off!", an automatic discount) Shopify does
   * NOT reduce final_line_price. Measured on an $849.99 bed rack with 10off! applied:
   *
   *     original_line_price 84999   final_line_price 84999   <- unchanged!
   *     line_price          76499   total_discount    8500
   *     line_level_discount_allocations  []          <- empty, it is cart-level
   *
   * So `original_line_price > final_line_price` is ALWAYS FALSE for cart-level discounts
   * and is the wrong test. `total_discount` is the reliable per-line signal, populated
   * for both cart-level and line-level discounts.
   * --------------------------------------------------------------------------------- */

  /* Itemised order summary from /cart.js - covers codes, automatic discounts and
   * app/Function discounts identically. The footer used to print total_price alone, so a
   * saved 10% code made an $849.99 bed rack read $765.00 with no explanation. */
  function summary(cart) {
    var origEl = drawer.querySelector('[data-cd-orig]');
    var rowsEl = drawer.querySelector('[data-cd-saverows]');
    var orig = cart.original_total_price != null ? cart.original_total_price : cart.total_price;

    if (origEl) origEl.textContent = money(orig);
    if (subEl) subEl.textContent = money(cart.total_price);
    if (!rowsEl) return;

    var byName = {}, order = [];
    function bump(t, amt) {
      t = t || 'Discount';
      if (byName[t] == null) { byName[t] = 0; order.push(t); }
      byName[t] += amt || 0;
    }
    (cart.items || []).forEach(function (i) {
      (i.line_level_discount_allocations || []).forEach(function (a) {
        bump(a.discount_application && a.discount_application.title, a.amount);
      });
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      bump(d.title, d.total_allocated_amount);
    });

    var total = cart.total_discount || 0;
    if (!total) { rowsEl.innerHTML = ''; rowsEl.hidden = true; return; }

    var named = order.reduce(function (s, t) { return s + byName[t]; }, 0);
    // If named rows do not reconcile with Shopify's total, show one honest "Savings"
    // line rather than a breakdown that does not add up.
    if (order.length && named === total) {
      rowsEl.innerHTML = order.map(function (t) {
        return '<div class="crled-cd__save"><span class="crled-cd__save-name">' + esc(t) +
          '</span><span class="crled-cd__save-amt">&minus;' + money(byName[t]) + '</span></div>';
      }).join('');
    } else {
      rowsEl.innerHTML = '<div class="crled-cd__save"><span class="crled-cd__save-name">Savings</span>' +
        '<span class="crled-cd__save-amt">&minus;' + money(total) + '</span></div>';
    }
    rowsEl.hidden = false;
  }

  /* --- "Customers Also Love" upsell row (2026-08-11) --------------------------------
   * Injected into the scrollable body AFTER the line items so it can never push
   * CHECKOUT off-screen. Seeded by the first real (non-coverage) line via Shopify's
   * native recommendations endpoint; cached per product so qty changes don't refetch.
   * Excludes anything already in the cart and Redo's coverage product. Single-variant
   * products quick-add in place; multi-variant products link to their page instead -
   * picking options inside a 52px card mis-sells.
   * Prices from /recommendations/products.json are CENTS (verified 2026-08-11:
   * 1999 -> $19.99), same unit as /cart.js, so money() applies directly.
   * renderToken guards the async append: a stale fetch must not write into a body
   * that render() has since rebuilt for a different cart state. */
  var recoCache = {};
  var renderToken = 0;
  function isCoverageProduct(p) {
    return (p.vendor && p.vendor.toLowerCase() === 're:do') ||
      p.handle === 'free-unlimited-return-valid-in-us';
  }
  function renderUpsell(cart, token) {
    if (!body || !cart || !cart.item_count) return;
    var seed = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isCoverage(cart.items[i])) { seed = cart.items[i]; break; }
    }
    if (!seed) return;
    var inCart = {};
    cart.items.forEach(function (i) { inCart[i.product_id] = true; });
    var ready = recoCache[seed.product_id]
      ? Promise.resolve(recoCache[seed.product_id])
      : fetch('/recommendations/products.json?product_id=' + seed.product_id + '&limit=8&intent=related',
          { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (d) {
            recoCache[seed.product_id] = (d && d.products) || [];
            return recoCache[seed.product_id];
          })
          .catch(function () { return []; });
    ready.then(function (prods) {
      if (token !== renderToken) return; // body was rebuilt since this fetch started
      var picks = prods.filter(function (p) {
        return !inCart[p.id] && p.available !== false && !isCoverageProduct(p);
      }).slice(0, 3);
      var old = body.querySelector('[data-cd-upsell]');
      if (old) old.remove();
      if (!picks.length) return;
      var html = picks.map(function (p) {
        var v1 = (p.variants && p.variants.length === 1) ? p.variants[0] : null;
        var img = p.featured_image || (p.images && p.images[0]) || '';
        var cta = v1
          ? '<button type="button" class="crled-cd__up-add" data-cd-upadd="' + v1.id + '">+ Add</button>'
          : '<a class="crled-cd__up-add" href="' + esc(p.url) + '">Options</a>';
        return '<div class="crled-cd__up-card">' +
          '<a class="crled-cd__up-imglink" href="' + esc(p.url) + '" tabindex="-1">' +
            (img ? '<img src="' + esc(String(img).replace(/(\.[a-z]+)(\?|$)/i, '_180x$1$2')) +
              '" alt="' + esc(p.title) + '" loading="eager" decoding="async" width="52" height="52">' : '') +
          '</a>' +
          '<div class="crled-cd__up-meta">' +
            '<a class="crled-cd__up-name" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
            '<span class="crled-cd__up-price">' + money(p.price) + '</span>' +
          '</div>' + cta + '</div>';
      }).join('');
      var wrap = document.createElement('div');
      wrap.setAttribute('data-cd-upsell', '');
      wrap.className = 'crled-cd__up';
      wrap.innerHTML = '<p class="crled-cd__up-h">Customers Also Love</p>' + html;
      body.appendChild(wrap);
    });
  }

  function render(cart) {
    if (!body) return;
    renderToken++;

    if (!cart || !cart.item_count) {
      body.innerHTML = '<p class="crled-cd__empty">Your cart is empty.</p>';
      if (foot) foot.hidden = true;
      setCount(0);
      return;
    }

    body.innerHTML = cart.items.map(function (i, idx) {
      var line = idx + 1;
      var cov = isCoverage(i);
      var variant = (i.variant_title && i.variant_title !== 'Default Title' && !cov)
        ? '<p class="crled-cd__var">' + esc(i.variant_title) + '</p>' : '';
      // loading="eager": the drawer sits at translateX(100%), outside the viewport, so
      // lazy images would not load until it slides in - a visible blank flash.
      var img = i.image
        ? '<img class="crled-cd__img" src="' + esc(i.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')) +
          '" alt="' + esc(i.product_title) + '" loading="eager" decoding="async" width="76" height="76">'
        : '<div class="crled-cd__img"></div>';

      // See the field notes above: total_discount, not final_line_price.
      var off = i.total_discount || 0;
      var was = (i.original_line_price != null) ? i.original_line_price : i.line_price;
      var price = off > 0
        ? '<span class="crled-cd__price is-disc"><span class="crled-cd__was">' +
          money(was) + '</span>' + money(was - off) + '</span>'
        : '<span class="crled-cd__price">' + money(i.line_price) + '</span>';

      // Only tag genuine LINE-level promos. A cart-level code is already named once in
      // the summary; repeating it on every row is noise.
      var tags = (i.line_level_discount_allocations || []).map(function (a) {
        var t = (a.discount_application && a.discount_application.title) || 'Discount';
        return '<span class="crled-cd__dtag">' + esc(t) + ' &minus;' + money(a.amount) + '</span>';
      }).join('');

      // Coverage gets a labelled Remove where the stepper would be, and no corner x -
      // one obvious control instead of two, and no meaningless quantity.
      var ctl = cov
        ? '<button type="button" class="crled-cd__covrm" data-cd-rm="' + line + '">Remove</button>'
        : '<span class="crled-cd__qty">' +
            '<button type="button" data-cd-dec="' + line + '" aria-label="Decrease quantity">-</button>' +
            '<span data-cd-qv>' + i.quantity + '</span>' +
            '<button type="button" data-cd-inc="' + line + '" aria-label="Increase quantity">+</button>' +
          '</span>';
      var corner = cov ? ''
        : '<button type="button" class="crled-cd__rm" data-cd-rm="' + line + '" aria-label="Remove item">&times;</button>';
      var name = cov
        ? '<span class="crled-cd__name">' + esc(i.product_title) + '</span>' +
          '<p class="crled-cd__var">Optional &middot; added automatically</p>'
        : '<a class="crled-cd__name" href="' + esc(i.url) + '">' + esc(i.product_title) + '</a>';

      return '<div class="crled-cd__item' + (cov ? ' crled-cd__item--cov' : '') + '">' + img +
        '<div class="crled-cd__meta">' + name + variant +
          '<div class="crled-cd__row">' + ctl + price + '</div>' + tags +
        '</div>' + corner +
        '</div>';
    }).join('');

    if (foot) foot.hidden = false;
    summary(cart);
    setCount(realCount(cart));
    renderUpsell(cart, renderToken);
  }

  function setCount(n) {
    if (hCount) hCount.textContent = n ? ' (' + n + ')' : '';
    document.querySelectorAll('[data-cd-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = !n;
    });
  }

  function sync() {
    if (window.CartJS && typeof CartJS.getCart === 'function') { try { CartJS.getCart(); } catch (e) {} }
  }

  function refresh(openAfter) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(cart); sync(); if (openAfter) open(); return cart; })
      .catch(function () {});
  }

  /* One delayed re-read after any cart mutation. Redo re-tiers its coverage line
   * (3% of cart across 96 price-tier variants) right AFTER our immediate refresh.
   * The badges are already race-immune (realCount ignores coverage entirely), but the
   * drawer CONTENTS - the coverage row's own price and the estimated total - lag one
   * tier behind until the next read. The timer collapses across rapid mutations so a
   * stepper spree ends with exactly one settling read. */
  var settleT;
  function settle() {
    clearTimeout(settleT);
    settleT = setTimeout(function () { refresh(false); }, 1400);
  }

  function change(line, qty) {
    if (busy) return;
    busy = true; drawer.classList.add('is-busy');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(cart); sync(); settle(); })
      .catch(function () {})
      .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
  }

  document.addEventListener('DOMContentLoaded', function () {
    drawer = document.querySelector('[data-cd]');
    overlay = document.querySelector('[data-cd-ov]');
    if (!drawer || !overlay) return;

    // #shopify-section-footer sets position:relative + z-index:2, creating a stacking
    // context in which our z-index is meaningless. Reparent to <body>.
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    body = drawer.querySelector('[data-cd-body]');
    foot = drawer.querySelector('[data-cd-foot]');
    subEl = drawer.querySelector('[data-cd-sub]');
    hCount = drawer.querySelector('[data-cd-hcount]');

    mountBadges();
    refresh(false);

    // Redo adds its coverage line on page load, after our first read, so the coverage
    // row and total could settle one short. Re-read once the page has finished loading
    // so the drawer contents agree.
    window.addEventListener('load', function () { refresh(false); });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

      var submitter = e.submitter || document.activeElement;
      var isBuyNow = !!(submitter && submitter.closest && submitter.closest('[data-crled-buy-now]'));

      e.preventDefault();
      if (busy) return;
      busy = true; drawer.classList.add('is-busy');

      fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function () {
          if (isBuyNow) { window.location.assign('/checkout'); return; }
          return refresh(true).then(function () { settle(); });
        })
        .catch(function () { form.submit(); })
        .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
    }, true);

    document.addEventListener('click', function (e) {
      // 2026-08-10 (Claude): never swallow a click the browser is meant to handle itself.
      // cmd/ctrl/shift/alt-click and any non-primary button now reach the browser, so
      // "open in new tab / new window" works on every link in and around the drawer.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // 2026-08-10 (Claude): the branch that used to stand here was
      //     var link = e.target.closest(CART_LINK);
      //     if (link) { e.preventDefault(); refresh(true); return; }
      // It preventDefault()ed EVERY <a href="*/cart"> on the site, so /cart was
      // unreachable - including from the header. At the time that also orphaned the
      // required attributes[location-type] select that only templates/cart.liquid
      // rendered, so cart links were switched to navigate normally.
      //
      // 2026-08-10 later (Claude): that select is now GONE - removed at Jake's request
      // together with cart.s.min.js's #checkout blocker - so the constraint above no
      // longer applies. Jake chose drawer-over-navigation for the HEADER ICONS ONLY.
      // 2026-08-11: desktop parity - the desktop header cart icon (li.cart-bg > a in
      // snippets/top-site-nav.liquid) now opens the drawer like the mobile icon does.
      // Every other cart link (mobile menu footer, cart page) still navigates, so
      // /cart stays reachable for its lead-time badges and shipping notes. Both icons
      // keep href="/cart" as the no-JS and modifier-click fallback, and the guard
      // above keeps cmd/ctrl-click native.
      var hdrCart = e.target.closest('.mobile-menu__cart, li.cart-bg > a[href$="/cart"]');
      if (hdrCart) { e.preventDefault(); refresh(true); return; }

      // Upsell quick-add (single-variant products only; multi-variant renders a link).
      var up = e.target.closest('[data-cd-upadd]');
      if (up) {
        if (busy) return;
        busy = true; drawer.classList.add('is-busy');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: parseInt(up.getAttribute('data-cd-upadd'), 10), quantity: 1 })
        })
          .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
          .then(function () { return refresh(false).then(function () { settle(); }); })
          .catch(function () {})
          .finally(function () { busy = false; drawer.classList.remove('is-busy'); });
        return;
      }

      if (e.target.closest('[data-cd-close]') || e.target.closest('[data-cd-ov]')) { close(); return; }
      var inc = e.target.closest('[data-cd-inc]');
      var dec = e.target.closest('[data-cd-dec]');
      var rm  = e.target.closest('[data-cd-rm]');
      if (inc || dec || rm) {
        var el = inc || dec || rm;
        var line = parseInt(el.getAttribute('data-cd-inc') || el.getAttribute('data-cd-dec') || el.getAttribute('data-cd-rm'), 10);
        if (rm) return change(line, 0);
        var wrap = el.closest('.crled-cd__qty');
        var qv = wrap ? wrap.querySelector('[data-cd-qv]') : null;
        var cur = qv ? parseInt(qv.textContent, 10) : 1;
        return change(line, inc ? cur + 1 : Math.max(0, cur - 1));
      }
    });

    document.addEventListener('keyup', function (e) { if (e.key === 'Escape') close(); });
  });
})();
